import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, X, ShoppingBag } from 'lucide-react';
interface Buyer {
  name: string;
  location: string;
  plan: 'Megapack Autonomía Total' | 'Megapack Autonomía Total PRO';
}
const BUYERS: Buyer[] = [{
  name: 'María José G.',
  location: 'Buenos Aires',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Lucas P.',
  location: 'Rosario, Santa Fe',
  plan: 'Megapack Autonomía Total'
}, {
  name: 'Camila S.',
  location: 'Córdoba Capital',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Federico R.',
  location: 'La Plata, BsAs',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Sofía L.',
  location: 'Mendoza',
  plan: 'Megapack Autonomía Total'
}, {
  name: 'Martín O.',
  location: 'Mar del Plata, BsAs',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Valeria T.',
  location: 'San Miguel de Tucumán',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Joaquín B.',
  location: 'Salta Capital',
  plan: 'Megapack Autonomía Total'
}, {
  name: 'Antonella V.',
  location: 'Neuquén',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Diego A.',
  location: 'Bahía Blanca, BsAs',
  plan: 'Megapack Autonomía Total'
}, {
  name: 'Florencia M.',
  location: 'San Juan',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Nicolás C.',
  location: 'Paraná, Entre Ríos',
  plan: 'Megapack Autonomía Total'
}, {
  name: 'Romina D.',
  location: 'Resistencia, Chaco',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Tomás H.',
  location: 'Posadas, Misiones',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Agustina F.',
  location: 'Santa Fe Capital',
  plan: 'Megapack Autonomía Total'
}, {
  name: 'Ezequiel N.',
  location: 'Quilmes, BsAs',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Brenda K.',
  location: 'San Luis',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Pablo I.',
  location: 'Bariloche, Río Negro',
  plan: 'Megapack Autonomía Total'
}, {
  name: 'Daniela Z.',
  location: 'Corrientes Capital',
  plan: 'Megapack Autonomía Total PRO'
}, {
  name: 'Sebastián Q.',
  location: 'Comodoro Rivadavia',
  plan: 'Megapack Autonomía Total'
}];
// Random integer in [min, max] inclusive
const randInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
export const SocialProofPopup = () => {
  const [current, setCurrent] = useState<(Buyer & {
    minutesAgo: number;
  }) | null>(null);
  const [visible, setVisible] = useState(false);
  const usedIndices = useRef<Set<number>>(new Set());
  // Pick a random buyer that hasn't been shown recently
  const pickBuyer = () => {
    // Reset cycle if all used
    if (usedIndices.current.size >= BUYERS.length) {
      usedIndices.current.clear();
    }
    let idx: number;
    do {
      idx = randInt(0, BUYERS.length - 1);
    } while (usedIndices.current.has(idx));
    usedIndices.current.add(idx);
    return {
      ...BUYERS[idx],
      minutesAgo: randInt(2, 28)
    };
  };
  useEffect(() => {
    let showTimeout: ReturnType<typeof setTimeout>;
    let hideTimeout: ReturnType<typeof setTimeout>;
    const showOne = () => {
      setCurrent(pickBuyer());
      setVisible(true);
      // Auto-hide after ~6s
      hideTimeout = setTimeout(() => {
        setVisible(false);
        // Schedule the next one: 40-60 seconds from now
        showTimeout = setTimeout(showOne, randInt(40000, 60000));
      }, 6500);
    };
    // First popup after 12 seconds
    showTimeout = setTimeout(showOne, 12000);
    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, []);
  const handleDismiss = () => setVisible(false);
  if (!current) return null;
  const initials = current.name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
  return <AnimatePresence data-id="element-311">
      {visible && <motion.div initial={{
      opacity: 0,
      y: 40,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20,
      x: -20
    }} transition={{
      type: 'spring',
      stiffness: 260,
      damping: 24
    }} className="fixed bottom-4 left-4 z-50 max-w-[88vw] sm:max-w-sm" role="status" aria-live="polite" data-id="element-312">
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 pl-3 pr-9 py-3 flex items-center gap-3 overflow-hidden" data-id="element-313">
            {/* Decorative accent stripe */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-teal to-gold" data-id="element-314"></div>

            {/* Avatar with check */}
            <div className="relative flex-shrink-0" data-id="element-315">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal to-navy text-white flex items-center justify-center font-bold text-sm shadow-md" data-id="element-316">
                {initials}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow" data-id="element-317">
                <CheckCircle2 className="w-4 h-4 text-green-500 fill-green-500/10" strokeWidth={2.5} data-id="element-318" />
              </div>
            </div>

            {/* Text */}
            <div className="min-w-0 flex-grow" data-id="element-319">
              <div className="flex items-center gap-1 mb-0.5" data-id="element-320">
                <ShoppingBag className="w-3 h-3 text-teal flex-shrink-0" data-id="element-321" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-teal" data-id="element-322">
                  Compra verificada
                </p>
              </div>
              <p className="text-[13px] sm:text-sm font-bold text-navy leading-tight truncate" data-id="element-323">
                {current.name} · {current.location}
              </p>
              <p className="text-[11px] text-gray-600 leading-tight truncate" data-id="element-324">
                Compró el{' '}
                <span className="font-semibold text-navy" data-id="element-325">{current.plan}</span>
              </p>
              <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1" data-id="element-326">
                <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" data-id="element-327"></span>
                Ahora mismo · Argentina 🇦🇷
              </p>
            </div>

            {/* Close button */}
            <button onClick={handleDismiss} aria-label="Cerrar notificación" className="absolute top-1.5 right-1.5 p-1 rounded-full hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-700" data-id="element-328">
              <X className="w-3.5 h-3.5" data-id="element-329" />
            </button>

            {/* Progress bar (auto-dismiss countdown) */}
            <motion.div key={current.name + current.minutesAgo} initial={{
          width: '100%'
        }} animate={{
          width: '0%'
        }} transition={{
          duration: 6.5,
          ease: 'linear'
        }} className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-teal to-gold" data-id="element-330"></motion.div>
          </div>
        </motion.div>}
    </AnimatePresence>;
};